import { supabase } from '../lib/supabase';

// ── Service Profile — Linked Partners ──────────────────────────────────────

export const serviceLinkedPartnersService = {
  async getByService(catalogServiceId) {
    const { data, error } = await supabase
      ?.from('partner_services')
      ?.select(`
        id,
        partner_id,
        coverage_area,
        delivery_capability,
        sla_commitment,
        link_status,
        notes,
        created_at,
        partners (
          id, legal_name, brand_name, tier, status, country
        )
      `)
      ?.eq('catalog_service_id', catalogServiceId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      partnerId: row?.partner_id,
      coverageArea: row?.coverage_area,
      deliveryCapability: row?.delivery_capability,
      slaCommitment: row?.sla_commitment,
      linkStatus: row?.link_status,
      notes: row?.notes,
      createdAt: row?.created_at,
      partnerName: row?.partners?.brand_name || row?.partners?.legal_name || '—',
      partnerTier: row?.partners?.tier,
      partnerStatus: row?.partners?.status,
      partnerCountry: row?.partners?.country,
    }));
  },

  async unlink(linkId) {
    const { error } = await supabase?.from('partner_services')?.delete()?.eq('id', linkId);
    if (error) throw error;
  },

  async countByService(catalogServiceId) {
    const { count, error } = await supabase
      ?.from('partner_services')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('catalog_service_id', catalogServiceId)
      ?.eq('link_status', 'active');
    if (error) throw error;
    return count || 0;
  },
};

// ── Service Lifecycle ───────────────────────────────────────────────────────

export const serviceLifecycleService = {
  async getByService(catalogServiceId) {
    const { data, error } = await supabase
      ?.from('service_lifecycle')
      ?.select(`
        id, stage, stage_date, notes, expected_eol_date, created_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_service_id', catalogServiceId)
      ?.order('stage_date', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      stage: row?.stage,
      stageDate: row?.stage_date,
      notes: row?.notes,
      expectedEolDate: row?.expected_eol_date,
      createdAt: row?.created_at,
      changedByName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'System',
    }));
  },

  async addStage(catalogServiceId, stageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('service_lifecycle')
      ?.insert({
        catalog_service_id: catalogServiceId,
        stage: stageData?.stage,
        stage_date: stageData?.stageDate || new Date()?.toISOString()?.split('T')?.[0],
        notes: stageData?.notes || null,
        expected_eol_date: stageData?.expectedEolDate || null,
        changed_by: user?.id || null,
      })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ── Service Updates / News Feed ─────────────────────────────────────────────

export const serviceUpdatesService = {
  async getByService(catalogServiceId) {
    const { data, error } = await supabase
      ?.from('service_updates')
      ?.select(`
        id, update_type, title, content, created_at, updated_at,
        user_profiles ( full_name, email )
      `)
      ?.eq('catalog_service_id', catalogServiceId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map(row => ({
      id: row?.id,
      updateType: row?.update_type,
      title: row?.title,
      content: row?.content,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      authorName: row?.user_profiles?.full_name || row?.user_profiles?.email || 'Admin',
    }));
  },

  async create(catalogServiceId, updateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('service_updates')
      ?.insert({
        catalog_service_id: catalogServiceId,
        update_type: updateData?.updateType || 'general',
        title: updateData?.title,
        content: updateData?.content || null,
        posted_by: user?.id || null,
      })
      ?.select(`id, update_type, title, content, created_at, user_profiles(full_name, email)`)
      ?.single();
    if (error) throw error;
    return {
      id: data?.id,
      updateType: data?.update_type,
      title: data?.title,
      content: data?.content,
      createdAt: data?.created_at,
      authorName: data?.user_profiles?.full_name || data?.user_profiles?.email || 'Admin',
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('service_updates')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ── Partner Count per Service ───────────────────────────────────────────────

export const servicePartnerCountService = {
  async getAllCounts() {
    const { data, error } = await supabase
      ?.from('partner_services')
      ?.select('catalog_service_id')
      ?.eq('link_status', 'active');
    if (error) return {};
    const counts = {};
    (data || [])?.forEach(row => {
      counts[row?.catalog_service_id] = (counts?.[row?.catalog_service_id] || 0) + 1;
    });
    return counts;
  },
};
